import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

// -----------------------------------------------------------------------------
// GeoSearchSelect — searchable dropdown backed by /api/geo/{comuni|stati}.
// Module-level so re-renders of the parent don't remount it (which would
// blur the CommandInput and destroy the query — same anti-pattern that bit
// us with `Field`/`Row` earlier). Pass:
//   - value          the currently selected ISTAT code (e.g. "100000100" | "H501")
//   - onChange(code, record)  fired on pick; `record` is the raw catalog row
//   - fetchUrl       "/geo/stati" or "/geo/comuni"
//   - displayKey     primary label field on the record ("name_it" | "name")
//   - extraKey       optional secondary label (right-aligned, dim: "name_en" | "prov")
// Extracted from ReceptionTab.jsx (Iteration 62 — modularisation).
// -----------------------------------------------------------------------------
export default function GeoSearchSelect({ value, onChange, fetchUrl, displayKey, extraKey, placeholder, testId, IT, className }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Backfill the displayed label when only a `value` (code) is known — either
  // because the form was pre-populated from OCR / a previous session, or
  // because the manual entry defaulted to Italy (100000100).
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected && selected.code === value) return;
    let cancelled = false;
    api.get(fetchUrl, { params: { q: value, limit: 5 } })
      .then((r) => {
        if (cancelled) return;
        const rec = (r.data || []).find((x) => x.code === value);
        if (rec) setSelected(rec);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, fetchUrl]);

  // Fetch matches when the popover is open — debounced so we don't spam the
  // backend while the host types. Empty query returns the top-of-catalog page.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(fetchUrl, { params: { q: query, limit: 30 } })
        .then((r) => { if (!cancelled) setResults(r.data || []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, query, fetchUrl]);

  const label = selected
    ? `${selected[displayKey]}${extraKey && selected[extraKey] ? ` (${selected[extraKey]})` : ""}`
    : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${!label ? "text-muted-foreground" : ""} ${className || ""}`}
          data-testid={testId}
        >
          <span className="truncate">{label || placeholder || (IT ? "Seleziona…" : "Select…")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={IT ? "Cerca…" : "Search…"}
            data-testid={`${testId}-input`}
          />
          <CommandList>
            {loading && (
              <div className="py-4 text-center text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-1 align-middle"/>
                {IT ? "Ricerca…" : "Searching…"}
              </div>
            )}
            {!loading && results.length === 0 && (
              <CommandEmpty>{IT ? "Nessun risultato" : "No results"}</CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={r.code}
                    value={r.code}
                    onSelect={() => {
                      setSelected(r);
                      onChange(r.code, r);
                      setOpen(false);
                      setQuery("");
                    }}
                    data-testid={`${testId}-item-${r.code}`}
                  >
                    <span className="truncate">{r[displayKey]}</span>
                    {extraKey && r[extraKey] ? (
                      <span className="ml-auto text-xs text-muted-foreground shrink-0">{r[extraKey]}</span>
                    ) : null}
                    {selected && selected.code === r.code && (
                      <Check className="ml-1 h-3.5 w-3.5 text-primary shrink-0"/>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
